import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  MessageSquare, Plus, TrendingUp, Clock, Users, Search, Filter,
  Settings, Eye, Heart,
  MessageCircle
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import CategoryList from './CategoryList';
import PostCard from './PostCard';
import NewPostForm from './NewPostForm';
import EditPostModal from './EditPostModal';
import CategoryManagement from './CategoryManagement';
import axios from 'axios';
import { toast } from 'react-hot-toast';

const ForumHome = () => {
  const navigate = useNavigate();
  const profile = useSelector(state => state.profile);
  const [showCategoryManagement, setShowCategoryManagement] = useState(false);

  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [showNewPostForm, setShowNewPostForm] = useState(false);
  const [showEditPostForm, setShowEditPostForm] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [pagination, setPagination] = useState({});
  const [stats, setStats] = useState({});

  // Fetch data
  useEffect(() => {
    fetchCategories();
    fetchPosts();
    fetchStats();
  }, [activeCategory, sortBy, searchQuery]);

  const fetchCategories = async () => {
    try {
      const { data } = await axios.get(`${process.env.REACT_APP_API_URL}/api/forumV3/categories`);
      if (data.success) {
        setCategories(data.categories);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Failed to load categories');
    }
  };

  const fetchPosts = async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        sort: sortBy,
        page: page.toString(),
        limit: '20'
      });

      if (activeCategory !== 'all') {
        params.append('category', activeCategory);
      }

      if (searchQuery) {
        params.append('search', searchQuery);
      }

      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/forumV3/posts?${params}`
      );
      
      if (data.success) {
        setPosts(page === 1 ? data.posts : [...posts, ...data.posts]);
        setPagination(data.pagination);
      }
    } catch (error) {
      console.error('Error fetching posts:', error);
      toast.error('Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const { data } = await axios.get(`${process.env.REACT_APP_API_URL}/api/forumV3/stats`);
      if (data.success) {
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleLikePost = async (postId) => {
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/forumV3/posts/${postId}/like`,
        { alumniId: profile._id }
      );
      
      if (data.success) {
        setPosts(posts.map(post => 
          post._id === postId 
            ? { ...post, likeCount: data.likeCount, isLikedByUser: data.isLiked }
            : post
        ));
        
        return { isLiked: data.isLiked, likeCount: data.likeCount };
      }
    } catch (error) {
      console.error('Error toggling like:', error);
      toast.error('Failed to like post');
    }
  };

  const handlePostCreated = (newPost) => {
    setPosts([newPost, ...posts]);
    setShowNewPostForm(false);
    fetchStats();
    fetchCategories();
     toast.dismiss() ;  toast.success('Post created successfully');
  };

  // CRITICAL: EDIT POST HANDLER
  const handleEditPost = (post) => {
    console.log('handleEditPost called with:', post); // Debug log
    setEditingPost(post);
    setShowEditPostForm(true);
  };

  // CRITICAL: POST UPDATED HANDLER
  const handlePostUpdated = (updatedPost) => {
    console.log('handlePostUpdated called with:', updatedPost); // Debug log
    setPosts(posts.map(post => 
      post._id === updatedPost._id ? updatedPost : post
    ));
    setShowEditPostForm(false);
    setEditingPost(null);
    fetchStats();
    fetchCategories();
     toast.dismiss() ;  toast.success('Post updated successfully');
  };

  // CRITICAL: DELETE POST HANDLER
  const handlePostDeleted = async (postId) => {
    console.log('handlePostDeleted called with:', postId); // Debug log
    try {
      const { data } = await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/forumV3/posts/${postId}`
      );
      
      if (data.success) {
        setPosts(posts.filter(post => post._id !== postId));
        fetchStats();
        fetchCategories();
         toast.dismiss() ;  toast.success('Post deleted successfully');
      } else {
        toast.error(data.message || 'Failed to delete post');
      }
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error('Failed to delete post');
    }
  };

  const handleCategoryCreated = () => {
    fetchCategories();
    fetchStats();
    setShowCategoryManagement(false);
     toast.dismiss() ;  toast.success('Category updated successfully');
  };

  const sortOptions = [
    { value: 'newest', label: 'Newest First' },
    { value: 'popular', label: 'Most Popular' },
    { value: 'trending', label: 'Trending' },
    { value: 'oldest', label: 'Oldest First' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 lg:px-6 py-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 dynamic-site-bg rounded-lg flex items-center justify-center">
              <MessageSquare size={16} className="text-white" />
            </div>
            <div>
              <h1 className="text-lg lg:text-2xl font-bold dynamic-site-text-1">Forum</h1>
              <p className="text-xs lg:text-sm text-gray-600 hidden sm:block">Connect with the community, share and discuss ideas</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {profile.admin && (
              <button
                onClick={() => setShowCategoryManagement(true)}
                className="flex items-center p-2 lg:px-3 lg:py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                <Settings size={16} />
                <span className="hidden lg:inline ml-2">Manage</span>
              </button>
            )}
            
            <button
              onClick={() => setShowNewPostForm(true)}
              className="flex items-center gap-2 px-3 py-2 dynamic-site-bg text-white rounded-lg hover:opacity-90 transition-opacity font-medium"
            >
              <Plus size={16} />
              <span className="hidden sm:inline">New Post</span>
            </button>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="flex gap-2 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search posts..."
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-sm"
            />
          </div>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white text-sm"
          >
            {sortOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

     

        {/* Main Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Sidebar */}
          <div className="hidden lg:block lg:col-span-1">
            <CategoryList
              categories={categories}
              activeCategory={activeCategory}
              onCategoryChange={setActiveCategory}
            />
          </div>

          {/* Mobile Categories */}
          <div className="lg:hidden mb-4 col-span-1">
            <div className="flex gap-2 overflow-x-auto pb-2">
              {[{ _id: 'all', name: 'All' }, ...categories.slice(0, 6)].map((category) => (
                <button
                  key={category._id}
                  onClick={() => setActiveCategory(category._id)}
                  className={`flex-shrink-0 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    activeCategory === category._id
                      ? 'dynamic-site-bg text-white'
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>

          {/* Main Content */}
          <div className="col-span-1 lg:col-span-3">
            {loading && posts.length === 0 ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="bg-white rounded-lg p-4 animate-pulse shadow-sm">
                    <div className="flex gap-3">
                      <div className="w-10 h-10 bg-gray-300 rounded-full"></div>
                      <div className="flex-1 space-y-2">
                        <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                        <div className="h-3 bg-gray-300 rounded w-1/2"></div>
                        <div className="h-3 bg-gray-300 rounded w-full"></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                {/* CRITICAL: PASS THE HANDLERS TO POSTCARD */}
                {posts.map((post) => (
                  <PostCard
                    key={post._id}
                    post={post}
                    onLike={() => handleLikePost(post._id)}
                    onComment={() => navigate(`/home/forum/posts/${post._id}`)}
                    onEdit={handleEditPost}  //{/* THIS IS KEY! */}
                    onDelete={handlePostDeleted}  //{/* THIS IS KEY! */}
                  />
                ))}

                {pagination.hasNext && (
                  <div className="text-center py-4">
                    <button
                      onClick={() => fetchPosts(pagination.current + 1)}
                      disabled={loading}
                      className="dynamic-site-bg text-white px-6 py-2 rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
                    >
                      {loading ? 'Loading...' : 'Load More'}
                    </button>
                  </div>
                )}

                {posts.length === 0 && !loading && (
                  <div className="text-center py-12 bg-white rounded-lg">
                    <MessageSquare size={48} className="mx-auto text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No posts found</h3>
                    <p className="text-gray-600 mb-4">Be the first to start a discussion!</p>
                    <button
                      onClick={() => setShowNewPostForm(true)}
                      className="dynamic-site-bg text-white px-4 py-2 rounded-lg font-medium"
                    >
                      Create Post
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* CRITICAL: MODALS FOR EDIT/CREATE */}
        <AnimatePresence>
          {showNewPostForm && (
            <NewPostForm
              categories={categories}
              onClose={() => setShowNewPostForm(false)}
              onPostCreated={handlePostCreated}
            />
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showEditPostForm && editingPost && (
            <EditPostModal
              post={editingPost}
              categories={categories}
              onClose={() => {
                setShowEditPostForm(false);
                setEditingPost(null);
              }}
              onPostUpdated={handlePostUpdated} // {/* THIS IS KEY! */}
            />
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showCategoryManagement && (
            <CategoryManagement
              onClose={() => setShowCategoryManagement(false)}
              onCategoryCreated={handleCategoryCreated}
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ForumHome;
